import React from 'react';
import { Link } from 'react-router-dom';
import { useScrollAnimation } from '../hooks/useScrollAnimation';
import { FaTruck, FaChargingStation, FaBolt, FaArrowRight, FaCheckCircle } from 'react-icons/fa';
import { trackMarketingFunnel } from '../utils/marketingAnalytics';

function Products() {
    const [headerRef, isHeaderVisible] = useScrollAnimation({ threshold: 0.15 });
    const [productsRef, areProductsVisible] = useScrollAnimation({ threshold: 0.15 });

    const products = [
        {
            id: 'efleetplan',
            name: 'eFleetPlan',
            tagline: 'Intelligent Fleet Electrification Planning',
            description: 'Comprehensive fleet analysis that leverages your routes and operations to provide accurate TCO estimates and optimization strategies for electric vehicle adoption.',
            icon: <FaTruck className="w-12 h-12" />,
            color: 'blue',
            gradient: 'from-blue-500 to-blue-600',
            bgGradient: 'from-blue-50 to-blue-100',
            borderColor: 'border-blue-200',
            features: [
                'Route-based fleet analysis and optimization',
                'Total Cost of Ownership (TCO) calculations',
                'Charging infrastructure planning',
                'Time-of-use rate optimization',
                'Solar and battery storage integration',
                'Multi-depot management'
            ],
            status: 'available',
            learnMoreLink: '/solutions/fleet',
            ctaLink: '/subscription?module=fleet',
            ctaText: 'View Plans'
        },
        {
            id: 'chargerplan',
            name: 'ChargerPlan',
            tagline: 'Strategic Charging Station Site Selection',
            description: 'Identify optimal locations for medium and heavy-duty charging stations with comprehensive spatial analysis, demand forecasting, and infrastructure evaluation.',
            icon: <FaChargingStation className="w-12 h-12" />,
            color: 'green',
            gradient: 'from-green-500 to-green-600',
            bgGradient: 'from-green-50 to-green-100',
            borderColor: 'border-green-200',
            features: [
                'Land parcel analysis and comparison',
                'Location demand analysis',
                'Grid capacity and infrastructure assessment',
                'Points of interest and trucking hotspot data',
                'Zoning and regulatory information',
                'Interactive map-based exploration'
            ],
            status: 'available',
            learnMoreLink: '/solutions/charging',
            ctaLink: '/contact',
            ctaText: 'Contact Sales'
        },
        {
            id: 'gridplan',
            name: 'GridPlan',
            tagline: 'Utility Infrastructure Planning',
            description: 'Plan grid upgrades and optimize utility operations for growing EV adoption with load forecasting, demand response, and smart charging strategies.',
            icon: <FaBolt className="w-12 h-12" />,
            color: 'purple',
            gradient: 'from-purple-500 to-purple-600',
            bgGradient: 'from-purple-50 to-purple-100',
            borderColor: 'border-purple-200',
            features: [
                'Grid capacity analysis',
                'Load forecasting and demand modeling',
                'Infrastructure upgrade planning',
                'Smart charging integration',
                'Cost-benefit analysis',
                'Reliability and resilience planning'
            ],
            status: 'coming-soon',
            learnMoreLink: '/utilities',
            ctaLink: '/contact',
            ctaText: 'Join Waitlist'
        }
    ];

    return (
        <div className="bg-[#f3f3f3] pb-4 pt-20 font-ui min-h-screen">
            {/* Header Section */}
            <div
                ref={headerRef}
                className={`max-w-7xl mx-auto px-5 py-10 bg-white rounded-[20px] p-12 m-12 lg:p-16 shadow-sm transition-all duration-1000 ease-out transform ${
                    isHeaderVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                }`}
            >
                <div className="text-center mb-8">
                    <h1 className="text-4xl font-bold text-gray-800 mb-4 font-ui">
                        Our Products
                    </h1>
                    <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                        Comprehensive planning solutions for transportation electrification. From fleet operations to charging infrastructure to grid planning, we provide the tools you need to make informed decisions.
                    </p>
                </div>
            </div>

            {/* Products Grid */}
            <div
                ref={productsRef}
                className={`max-w-7xl mx-auto px-5 mb-12 transition-all duration-1000 ease-out transform ${
                    areProductsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                }`}
            >
                <div className="grid grid-cols-1 lg:grid-cols-1 gap-8">
                    {products.map((product, index) => (
                        <div
                            key={product.id}
                            className={`bg-gradient-to-br ${product.bgGradient} rounded-xl p-8 shadow-lg border-2 ${product.borderColor} relative overflow-hidden transition-all duration-300 hover:shadow-xl hover:-translate-y-1`}
                            style={{
                                transitionDelay: areProductsVisible ? `${index * 200}ms` : '0ms'
                            }}
                        >
                            {/* Status Badge */}
                            {product.status === 'coming-soon' && (
                                <div className="absolute top-4 right-4 bg-gray-600 text-white text-xs font-semibold px-3 py-1 rounded-full">
                                    Coming Soon
                                </div>
                            )}

                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                                {/* Left Column - Info */}
                                <div>
                                    <div className={`text-${product.color}-600 mb-4`}>
                                        {product.icon}
                                    </div>
                                    <h2 className="text-3xl font-bold text-gray-800 mb-2 font-ui">
                                        {product.name}
                                    </h2>
                                    <p className="text-lg font-semibold text-gray-700 mb-4">
                                        {product.tagline}
                                    </p>
                                    <p className="text-gray-700 mb-6 leading-relaxed">
                                        {product.description}
                                    </p>
                                    <div className="flex gap-4">
                                        <Link
                                            to={product.learnMoreLink}
                                            className={`inline-flex items-center px-6 py-3 bg-gradient-to-r ${product.gradient} text-white rounded-lg font-semibold hover:opacity-90 transition-opacity`}
                                        >
                                            Learn More
                                            <FaArrowRight className="ml-2" />
                                        </Link>
                                        <Link
                                            to={product.ctaLink}
                                            className={`inline-flex items-center px-6 py-3 bg-white border-2 ${product.borderColor} text-gray-700 rounded-lg font-semibold hover:bg-gray-50 transition-colors`}
                                        >
                                            {product.ctaText}
                                        </Link>
                                    </div>
                                    {product.id === 'efleetplan' && (
                                        <p className="mt-4 text-sm text-gray-600">
                                            Need a quick baseline first?{' '}
                                            <Link
                                                to="/fleet-planning-basic"
                                                onClick={() => trackMarketingFunnel('products_efleetplan_quick_estimate_click')}
                                                className="font-semibold text-blue-600 hover:text-blue-700"
                                            >
                                                Try the free EV vs diesel estimate
                                            </Link>{' '}
                                            powered by eFleetPlan.
                                        </p>
                                    )}
                                </div>

                                {/* Right Column - Features */}
                                <div>
                                    <h3 className="text-xl font-bold text-gray-800 mb-4">Key Features</h3>
                                    <ul className="space-y-3">
                                        {product.features.map((feature, idx) => (
                                            <li key={idx} className="flex items-start">
                                                <FaCheckCircle className={`w-5 h-5 text-${product.color}-600 mr-3 mt-0.5 flex-shrink-0`} />
                                                <span className="text-gray-700">{feature}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}

export default Products;